import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Button,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  Badge,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { FiCalendar, FiSave, FiX, FiInfo, FiCreditCard } from "react-icons/fi";
import { useGetPayrollPeriodSettings, useUpdatePayrollPeriodSettings } from "../../services/payrollService";
import { useUserContext } from "../../context/UserContext";
import { useAccessControl } from "../../hooks/useAccessControl";
import PaymentMethodPreference from "../../components/directDebit/PaymentMethodPreference";

const PayrollSettings = () => {
  const toast = useToast();
  const { user } = useUserContext();
  const { isCompanyAdmin } = useAccessControl();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const [activeTab, setActiveTab] = useState(0);

  // Check for pending mandate on mount and switch to Payment Method tab
  useEffect(() => {
    const pendingMandateCode = sessionStorage.getItem('pending_mandate_code') || 
                               localStorage.getItem('pending_mandate_code_backup');
    if (pendingMandateCode) {
      // Switch to Payment Method tab (index 1)
      setActiveTab(1);
    }
  }, []);

  // Fetch current settings
  const { data: settingsData, isLoading, error, refetch } = useGetPayrollPeriodSettings();
  const { updatePayrollPeriodSettings, updatePayrollPeriodSettingsIsLoading } = useUpdatePayrollPeriodSettings(() => {
    refetch();
  });

  // Form state
  const [formData, setFormData] = useState({
    period_start_date: "",
    period_end_date: "",
    use_custom_period: false,
    period_name: "",
  });

  const [errors, setErrors] = useState({});

  // Initialize form with fetched data
  useEffect(() => {
    if (settingsData) {
      const data = settingsData?.data || settingsData;
      setFormData({
        period_start_date: data.period_start_date
          ? new Date(data.period_start_date).toISOString().split("T")[0]
          : "",
        period_end_date: data.period_end_date
          ? new Date(data.period_end_date).toISOString().split("T")[0]
          : "",
        use_custom_period: data.use_custom_period || false,
        period_name: data.period_name || "",
      });
    }
  }, [settingsData]);

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (formData.use_custom_period) {
      if (!formData.period_start_date) {
        newErrors.period_start_date = "Start date is required";
      }
      if (!formData.period_end_date) {
        newErrors.period_end_date = "End date is required";
      }
      if (formData.period_start_date && formData.period_end_date) {
        const startDate = new Date(formData.period_start_date);
        const endDate = new Date(formData.period_end_date);
        if (endDate <= startDate) {
          newErrors.period_end_date = "End date must be after start date";
        }
      }
      if (!formData.period_name || formData.period_name.trim() === "") {
        newErrors.period_name = "Period name is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      period_start_date: formData.use_custom_period ? formData.period_start_date : null,
      period_end_date: formData.use_custom_period ? formData.period_end_date : null,
      use_custom_period: formData.use_custom_period,
      period_name: formData.use_custom_period ? formData.period_name : null,
    };

    updatePayrollPeriodSettings(payload);
  };

  const handleReset = () => {
    if (settingsData) {
      const data = settingsData?.data || settingsData;
      setFormData({
        period_start_date: data.period_start_date
          ? new Date(data.period_start_date).toISOString().split("T")[0]
          : "",
        period_end_date: data.period_end_date
          ? new Date(data.period_end_date).toISOString().split("T")[0]
          : "",
        use_custom_period: data.use_custom_period || false,
        period_name: data.period_name || "",
      });
    }
    setErrors({});
  };

  // Calculate current period dates for display
  const getCurrentPeriodDates = () => {
    if (formData.use_custom_period && formData.period_start_date && formData.period_end_date) {
      return {
        start: new Date(formData.period_start_date).toLocaleDateString(),
        end: new Date(formData.period_end_date).toLocaleDateString(),
      };
    }
    // Default to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: firstDay.toLocaleDateString(),
      end: lastDay.toLocaleDateString(),
    };
  };

  const currentPeriod = getCurrentPeriodDates();

  const { canViewPayrollSettings, canUpdatePayrollSettings } = useAccessControl();
  
  // Check if user can view settings
  if (!canViewPayrollSettings()) {
    return (
      <Box w="100%" bg="gray.50" minH="100vh" p={6}>
        <Card>
          <CardBody>
            <Alert status="error">
              <AlertIcon />
              <AlertDescription>
                You don't have permission to access payroll settings.
              </AlertDescription>
            </Alert>
          </CardBody>
        </Card>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box w="100%" bg="gray.50" minH="100vh" p={6}>
        <Card>
          <CardBody>
            <Text>Loading payroll settings...</Text>
          </CardBody>
        </Card>
      </Box>
    );
  }

  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={2}>
            <Heading size="lg" color="gray.900" fontWeight="600">
              Payroll Settings
            </Heading>
            <Text color="gray.600" fontSize="md">
              Configure payroll settings, payment methods, and period dates
            </Text>
          </VStack>
        </HStack>

        {/* Settings Tabs */}
        <Card>
          <CardBody p={0}>
            <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed" colorScheme="blue">
              <TabList bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                <Tab
                  _selected={{ bg: "white", borderBottomColor: "white" }}
                  _hover={{ bg: "gray.100" }}
                  py={4}
                  px={6}
                >
                  <HStack spacing={2}>
                    <FiCalendar size={18} />
                    <Text fontWeight="500">Period Settings</Text>
                  </HStack>
                </Tab>
                <Tab
                  _selected={{ bg: "white", borderBottomColor: "white" }}
                  _hover={{ bg: "gray.100" }}
                  py={4}
                  px={6}
                >
                  <HStack spacing={2}>
                    <FiCreditCard size={18} />
                    <Text fontWeight="500">Payment Method</Text>
                  </HStack>
                </Tab>
              </TabList>

              <TabPanels>
                {/* Period Settings Tab */}
                <TabPanel p={6}>
                  <VStack spacing={6} align="stretch">

        {/* Current Period Display */}
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack>
                <FiInfo size={20} color="gray.600" />
                <Heading size="md" color="gray.900">
                  Current Payroll Period
                </Heading>
              </HStack>
              <HStack spacing={4}>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.600">
                    Period Type
                  </Text>
                  <Badge
                    colorScheme={formData.use_custom_period ? "blue" : "gray"}
                    fontSize="sm"
                    px={3}
                    py={1}
                  >
                    {formData.use_custom_period
                      ? formData.period_name || "Custom Period"
                      : "Calendar Month"}
                  </Badge>
                </VStack>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.600">
                    Start Date
                  </Text>
                  <Text fontSize="md" fontWeight="600" color="gray.900">
                    {currentPeriod.start}
                  </Text>
                </VStack>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.600">
                    End Date
                  </Text>
                  <Text fontSize="md" fontWeight="600" color="gray.900">
                    {currentPeriod.end}
                  </Text>
                </VStack>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Settings Form */}
        <Card>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Heading size="md" color="gray.900">
                Period Configuration
              </Heading>

              {/* Custom Period Toggle */}
              <FormControl>
                <HStack justify="space-between">
                  <VStack align="start" spacing={1}>
                    <FormLabel mb={0} fontWeight="500">
                      Use Custom Payroll Period
                    </FormLabel>
                    <Text fontSize="sm" color="gray.600">
                      Enable to set custom start and end dates instead of calendar month
                    </Text>
                  </VStack>
                  <Switch
                    isChecked={formData.use_custom_period}
                    onChange={(e) =>
                      handleInputChange("use_custom_period", e.target.checked)
                    }
                    colorScheme="blue"
                    size="lg"
                    isDisabled={!canUpdatePayrollSettings()}
                  />
                </HStack>
              </FormControl>

              <Divider />

              {/* Custom Period Fields */}
              {formData.use_custom_period && (
                <VStack spacing={4} align="stretch">
                  {/* Period Name */}
                  <FormControl isRequired isInvalid={!!errors.period_name}>
                    <FormLabel>Period Name</FormLabel>
                    <Input
                      placeholder="e.g., Mid-month Period, Bi-weekly"
                      value={formData.period_name}
                      onChange={(e) =>
                        handleInputChange("period_name", e.target.value)
                      }
                      isReadOnly={!canUpdatePayrollSettings()}
                      bg={!canUpdatePayrollSettings() ? 'gray.50' : 'white'}
                    />
                    {errors.period_name && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        {errors.period_name}
                      </Text>
                    )}
                  </FormControl>

                  {/* Start Date */}
                  <FormControl isRequired isInvalid={!!errors.period_start_date}>
                    <FormLabel>Period Start Date</FormLabel>
                    <Input
                      type="date"
                      value={formData.period_start_date}
                      onChange={(e) =>
                        handleInputChange("period_start_date", e.target.value)
                      }
                      isReadOnly={!canUpdatePayrollSettings()}
                      bg={!canUpdatePayrollSettings() ? 'gray.50' : 'white'}
                    />
                    {errors.period_start_date && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        {errors.period_start_date}
                      </Text>
                    )}
                  </FormControl>

                  {/* End Date */}
                  <FormControl isRequired isInvalid={!!errors.period_end_date}>
                    <FormLabel>Period End Date</FormLabel>
                    <Input
                      type="date"
                      value={formData.period_end_date}
                      onChange={(e) =>
                        handleInputChange("period_end_date", e.target.value)
                      }
                      min={formData.period_start_date || undefined}
                      isReadOnly={!canUpdatePayrollSettings()}
                      bg={!canUpdatePayrollSettings() ? 'gray.50' : 'white'}
                    />
                    {errors.period_end_date && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        {errors.period_end_date}
                      </Text>
                    )}
                    {formData.period_start_date && !errors.period_end_date && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Must be after start date: {new Date(formData.period_start_date).toLocaleDateString()}
                      </Text>
                    )}
                  </FormControl>
                </VStack>
              )}

              {/* Action Buttons */}
              {canUpdatePayrollSettings() && (
                <HStack spacing={3} pt={4}>
                  <Button
                    leftIcon={<FiSave />}
                    colorScheme="blue"
                    onClick={handleSubmit}
                    isLoading={updatePayrollPeriodSettingsIsLoading}
                    loadingText="Saving..."
                  >
                    Save Settings
                  </Button>
                  <Button
                    leftIcon={<FiX />}
                    variant="outline"
                    onClick={handleReset}
                    isDisabled={updatePayrollPeriodSettingsIsLoading}
                  >
                    Reset
                  </Button>
                </HStack>
              )}
              {!canUpdatePayrollSettings() && (
                <Alert status="info" borderRadius="lg" mt={4}>
                  <AlertIcon />
                  <AlertDescription>
                    You have read-only access to payroll settings. Only Company Administrators can modify these settings.
                  </AlertDescription>
                </Alert>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Info Alert */}
        <Alert status="info">
          <AlertIcon />
          <AlertDescription>
            <Text fontWeight="600" mb={1}>
              How Payroll Periods Work:
            </Text>
            <Text fontSize="sm">
              • <strong>Calendar Month:</strong> Payroll is calculated from the 1st to the last day of each month
              <br />
              • <strong>Custom Period:</strong> Set your own start and end dates (e.g., 15th to 14th of next month)
              <br />
              • All payroll calculations will use the configured period dates
              <br />
              • Each tenant can have its own payroll period configuration
            </Text>
          </AlertDescription>
        </Alert>
                  </VStack>
                </TabPanel>

                {/* Payment Method Tab */}
                <TabPanel p={6}>
                  <PaymentMethodPreference />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default PayrollSettings;

